import { ReactNodeViewRenderer } from "@tiptap/react"
import type { ImageOptions } from "@tiptap/extension-image"
import { Image as TiptapImage } from "@tiptap/extension-image"
import { ImageNodeView } from "./image-node-view"
import type { Node } from "@tiptap/pm/model"
import { TextSelection } from "@tiptap/pm/state"
import { Plugin, PluginKey } from "prosemirror-state"
import { mergeBlockIndentAttrs } from "../../../../app/javascript/lib/post_block_indent.js"

interface ImageAttributes {
  src: string | null
  alt?: string | null
  title?: string | null
  width?: string | null
  height?: string | null
  aspectRatio?: string | null
  "data-align"?: string | null
  resourceId?: string | number | null
}

const parseImageAttributes = (img: Element): Partial<ImageAttributes> => ({
  src: img.getAttribute("src"),
  alt: img.getAttribute("alt"),
  title: img.getAttribute("title"),
  width: img.getAttribute("width"),
  height: img.getAttribute("height"),
  aspectRatio:
    (img instanceof HTMLElement && (img.style as any)?.aspectRatio) ||
    img.getAttribute("data-aspect-ratio") ||
    null,
})

function buildImageHTMLAttributes(
  attrs: ImageAttributes
): Record<string, string> {
  const result: Record<string, string> = { src: attrs.src || "" }

  if (attrs.alt) result.alt = attrs.alt
  if (attrs.title) result.title = attrs.title
  if (attrs.width) result.width = attrs.width
  if (attrs.aspectRatio) {
    result.style = `aspect-ratio: ${attrs.aspectRatio}; height: auto;`
    result["data-aspect-ratio"] = attrs.aspectRatio
  } else if (attrs.height) {
    result.height = attrs.height
  }

  return result
}

export const Image = TiptapImage.extend<ImageOptions>({
  content: "inline*",

  // Use the React node view (ResizableImage) for resize UX — not @tiptap/core ResizableNodeView.
  addOptions() {
    return {
      ...this.parent?.(),
      resize: false,
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      aspectRatio: {
        default: null,
      },
      href: {
        default: null,
      },
      "data-align": {
        default: null,
      },
      resourceId: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-resource-id") ||
          element.closest?.("figure")?.getAttribute("data-resource-id") ||
          null,
        renderHTML: (attributes) => {
          if (attributes.resourceId == null || attributes.resourceId === "")
            return {}
          return { "data-resource-id": String(attributes.resourceId) }
        },
      },
      showCaption: {
        default: false,
        parseHTML: (element) => {
          return (
            element.tagName === "FIGURE" ||
            element.getAttribute("data-show-caption") === "true"
          )
        },
        renderHTML: (attributes) => {
          if (!attributes.showCaption) return {}
          return { "data-show-caption": "true" }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (node) => {
          const img = node.querySelector("img")
          if (!img) return false

          // Try to find an anchor wrapping the image to extract href
          const anchor = img.closest("a")
          const href = anchor ? anchor.getAttribute("href") : node.getAttribute("data-href")
          const resourceId =
            node.getAttribute("data-resource-id") ||
            img.getAttribute("data-resource-id") ||
            null

          return {
            ...parseImageAttributes(img),
            href: href || null,
            "data-align": node.getAttribute("data-align"),
            resourceId,
            showCaption: true,
          }
        },
        contentElement: "figcaption",
      },
      {
        tag: "img[src]",
        getAttrs: (node) => {
          if (node.closest("figure")) return false

          // If the image is wrapped in an anchor, capture its href
          const anchor = node.closest("a")
          const href = anchor ? anchor.getAttribute("href") : node.getAttribute("data-href")
          const resourceId =
            node.getAttribute("data-resource-id") ||
            node.closest?.("figure")?.getAttribute("data-resource-id") ||
            null

          return {
            ...parseImageAttributes(node),
            href: href || null,
            "data-align": node.getAttribute("data-align"),
            resourceId,
            showCaption: false,
          }
        },
      },
    ]
  },

  renderHTML({ node }) {
    const { src, alt, title, width, height, aspectRatio, showCaption, indent } =
      node.attrs
    const href = node.attrs.href
    const align = node.attrs["data-align"]
    const resourceId = node.attrs.resourceId

    const imgAttrs = buildImageHTMLAttributes({
      src,
      alt,
      title,
      width,
      height,
      aspectRatio,
    })
    imgAttrs.class = "post-content-image-img"

    const figureAttrs: Record<string, string> = {
      "data-url": src || "",
      "data-tiptap-image": "1",
      class: "post-content-image",
    }
    if (showCaption) figureAttrs["data-show-caption"] = "true"
    if (align) figureAttrs["data-align"] = align
    if (resourceId != null && resourceId !== "")
      figureAttrs["data-resource-id"] = String(resourceId)
    if (typeof indent === "number" && indent > 0)
      figureAttrs["data-ps-indent"] = String(indent)

    if (href) {
      // Always open image links in a new tab for safety/usability
      const anchorAttrs: Record<string, string> = {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
      }

      return [
        "figure",
        figureAttrs,
        ["a", anchorAttrs, ["img", imgAttrs]],
        ["figcaption", { class: "image-caption-container ps-post-style-caption" }, 0],
      ]
    }

    return [
      "figure",
      figureAttrs,
      ["img", imgAttrs],
      ["figcaption", { class: "image-caption-container ps-post-style-caption" }, 0],
    ]
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImage:
        (options) =>
        ({ commands, state }) => {
          return commands.insertContent({
            type: this.name,
            attrs: mergeBlockIndentAttrs(state, options || {}),
          })
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "Mod-a": ({ editor }) => {
        const { state, view } = editor
        const { selection } = state
        const { $from } = selection

        let imagePos: number | null = null
        let imageNode: Node | null = null

        for (let depth = $from.depth; depth >= 0; depth--) {
          const nodeAtDepth = $from.node(depth)
          if (nodeAtDepth.type === this.type) {
            imageNode = nodeAtDepth
            // posBefore is the resolved position *before* this node
            imagePos = depth === 0 ? 0 : $from.before(depth)
            break
          }
        }

        // Not inside an Image → let default behavior happen
        if (!imageNode || imagePos == null) {
          return false
        }

        // If the caption/content is empty, allow the default progressive select-all
        const contentIsEmpty =
          imageNode.content.size === 0 || imageNode.textContent.length === 0

        if (contentIsEmpty) {
          return false
        }

        // Compute the content range of the image node:
        // content starts at (nodePos + 1) and ends at (nodePos + node.nodeSize - 1)
        const start = imagePos + 1
        const end = imagePos + imageNode.nodeSize - 1

        const tr = state.tr.setSelection(
          TextSelection.create(state.doc, start, end)
        )
        view.dispatch(tr)

        return true
      },
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('imageCaptionEnterHandler'),
        props: {
          handleKeyDown: (view, event) => {
            // Handle backspace in empty paragraph after image
            if (event.key === 'Backspace') {
              const { state } = view
              const { selection } = state
              const { $from } = selection
              
              // Check if we're in an empty paragraph
              const currentNode = $from.node($from.depth)
              
              if (currentNode.type.name !== 'paragraph') {
                return false
              }
              
              const isEmpty = currentNode.content.size === 0 || 
                             (currentNode.content.size <= 2 && currentNode.textContent.trim() === '')
              
              if (!isEmpty) {
                return false
              }
              
              // Check if we're at the start of the paragraph
              const isAtStart = $from.parentOffset === 0 || 
                               ($from.pos === $from.start($from.depth) && currentNode.content.size === 0);
              
              if (!isAtStart) {
                return false
              }
              
              // Find the paragraph position
              const paragraphPos = $from.before($from.depth)
              
              if (paragraphPos <= 0) {
                return false
              }
              
              // Check if we're inside a list
              let isInList = false;
              let listItemNode: Node | null = null;
              let listItemPos: number | null = null;
              for (let depth = $from.depth; depth >= 0; depth--) {
                const nodeAtDepth = $from.node(depth);
                if (nodeAtDepth.type.name === 'listItem') {
                  isInList = true;
                  listItemNode = nodeAtDepth;
                  // Use before() to get the position before the listItem node
                  listItemPos = depth === 0 ? 0 : $from.before(depth);
                  break;
                }
              }
              
              let foundImage: Node | null = null
              let foundImagePos: number | null = null
              
              if (isInList && listItemNode && listItemPos !== null) {
                // Inside list: check siblings within the same listItem
                // Use the listItemNode we already found (from $from.node(depth))
                // Find paragraph index within listItem
                let paragraphIndex = -1;
                const listItemStart = listItemPos + 1; // Content starts after the listItem node
                let currentPos = listItemStart;
                
                // paragraphPos is before the paragraph node, so the paragraph starts at paragraphPos + 1
                const paragraphStartPos = paragraphPos + 1;
                for (let i = 0; i < listItemNode.content.size; i++) {
                  const child = listItemNode.content.child(i);
                  // Only match if it's actually a paragraph and the position matches
                  if (child.type.name === 'paragraph' && (currentPos === paragraphStartPos || Math.abs(currentPos - paragraphStartPos) < 5)) {
                    paragraphIndex = i;
                    break;
                  }
                  currentPos += child.nodeSize;
                }
                
                // Check previous siblings in the listItem
                if (paragraphIndex > 0) {
                  for (let i = paragraphIndex - 1; i >= 0; i--) {
                    const sibling = listItemNode.content.child(i);
                    
                    if (sibling.type === this.type) {
                      // Calculate position
                      let siblingPos = listItemStart;
                      for (let j = 0; j < i; j++) {
                        siblingPos += listItemNode.content.child(j).nodeSize;
                      }
                      foundImage = sibling;
                      foundImagePos = siblingPos;
                      break;
                    }
                    
                    if (sibling.isBlock) {
                      break;
                    }
                  }
                }
              } else {
                // Not in list: check if the node immediately before the paragraph is an image
                let prevBlockPos = paragraphPos - 1
                while (prevBlockPos >= 0) {
                  const nodeAtPos = state.doc.nodeAt(prevBlockPos)
                  if (!nodeAtPos) break
                  
                  // If we find an image node, handle backspace
                  if (nodeAtPos.type === this.type) {
                    foundImage = nodeAtPos;
                    foundImagePos = prevBlockPos;
                    break;
                  }
                  
                  // If it's a block node but not an image, stop
                  if (nodeAtPos.isBlock) break
                  
                  // Move to the start of this node
                  prevBlockPos = prevBlockPos - nodeAtPos.nodeSize
                }
              }
              
              if (foundImage && foundImagePos !== null) {
                // Prevent default behavior
                event.preventDefault();
                event.stopPropagation();
                
                // Delete the empty paragraph
                const tr = state.tr
                tr.delete(paragraphPos, paragraphPos + currentNode.nodeSize)
                
                // Move cursor to the image caption area
                const imageNode = tr.doc.nodeAt(foundImagePos);
                if (imageNode) {
                  // Position inside the image's caption (inline content)
                  const captionPos = foundImagePos + 1;
                  try {
                    const captionResolved = tr.doc.resolve(captionPos);
                    if (captionResolved) {
                      tr.setSelection(TextSelection.near(captionResolved));
                    }
                  } catch (e) {
                    // Fallback: set selection at image position
                    const imageResolved = tr.doc.resolve(foundImagePos);
                    if (imageResolved) {
                      tr.setSelection(TextSelection.near(imageResolved));
                    }
                  }
                }
                
                tr.scrollIntoView();
                view.dispatch(tr);
                
                return true
              }
              
              return false
            }
            
            if (event.key !== 'Enter') return false
            
            const { state } = view
            const { selection } = state
            const { $from } = selection
            
            // Check if we're inside an image node
            let imageNode: Node | null = null
            let imagePos: number | null = null
            let imageDepth = -1
            
            for (let depth = $from.depth; depth >= 0; depth--) {
              const nodeAtDepth = $from.node(depth)
              if (nodeAtDepth.type === this.type) {
                imageNode = nodeAtDepth
                imagePos = depth === 0 ? 0 : $from.before(depth)
                imageDepth = depth
                break
              }
            }
            
            if (!imageNode || imagePos === null) return false
            
            // Check if we're in the caption (inline content of image)
            const isInCaption = $from.depth === imageDepth || 
                              ($from.depth > imageDepth && $from.parent.type === imageNode.type)
            
            if (!isInCaption) return false
            
            // If Shift+Enter, allow default behavior (inserts hardBreak)
            // Check this early to avoid any interference
            // Use explicit check to handle cases where shiftKey might be undefined
            if (event.shiftKey === true || event.getModifierState?.('Shift') === true) {
              return false
            }
            
            // Check if caption is empty or at end
            const captionContent = imageNode.content
            const captionText = imageNode.textContent || ''
            const isAtEnd = $from.pos >= imagePos + imageNode.nodeSize - 1
            const isEmpty = captionContent.size === 0 ||
                           (captionContent.size <= 2 && captionText.trim() === '')
            
            if (!isAtEnd && !isEmpty) return false
            
            // Find list item
            let listItemPos: number | null = null
            let listItemNode: Node | null = null
            
            const imageResolved = state.doc.resolve(imagePos)
            for (let depth = imageResolved.depth; depth >= 0; depth--) {
              const nodeAtDepth = imageResolved.node(depth)
              if (nodeAtDepth.type.name === 'listItem') {
                listItemPos = imageResolved.start(depth)
                listItemNode = nodeAtDepth
                break
              }
            }
            
            if (listItemNode && listItemPos !== null) {
              // Inside list - insert paragraph after image within list item
              // Use $from.after() like download/meeting rows to preserve list context
              const afterPos = imageDepth !== null ? $from.after(imageDepth) : (imagePos + imageNode.nodeSize)
              
              // Use insertContentAt to preserve list structure
              const editor = (view as any).editor || (window as any).currentTiptapEditor
              if (editor) {
                editor.chain().focus().insertContentAt(afterPos, { type: 'paragraph' }).command(({ tr, dispatch }) => {
                  if (!dispatch) return false
                  // Set selection directly to the new paragraph to preserve list context
                  const paragraphNode = tr.doc.nodeAt(afterPos)
                  if (paragraphNode && paragraphNode.type.name === 'paragraph') {
                    const resolvedPos = tr.doc.resolve(afterPos + 1)
                    tr.setSelection(TextSelection.near(resolvedPos))
                    tr.scrollIntoView()
                  }
                  return true
                }).run()
                return true
              }
              // Fallback: use tr.insert if editor not available
              const tr = state.tr
              const paragraph = state.schema.nodes.paragraph.create()
              tr.insert(afterPos, paragraph)
              const resolvedPos = tr.doc.resolve(afterPos + 1)
              tr.setSelection(TextSelection.near(resolvedPos))
              tr.scrollIntoView()
              view.dispatch(tr)
              return true
            } else {
              // Not in list - insert paragraph after image
              const afterPos = imagePos + imageNode.nodeSize
              const tr = state.tr
              const paragraph = state.schema.nodes.paragraph.create()
              tr.insert(afterPos, paragraph)
              const resolvedPos = tr.doc.resolve(afterPos + 1)
              tr.setSelection(TextSelection.near(resolvedPos))
              tr.scrollIntoView()
              view.dispatch(tr)
              return true
            }
            
            return false
          },
        },
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView, {
      stopEvent: ({ event }) => {
        const t = event.type
        // Let drag events fall through to editor handlers
        if (/dragstart|dragover|dragend|drop/.test(t)) return false

        if (/mousedown|click|pointerdown|touchstart/.test(t)) return true

        return false
      },
    })
  },
})

export default Image
